import type { Me, Role } from "@elektr/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { api, AUTH_LOST_EVENT, tokens, unwrap } from "./api";

interface AuthValue {
  user: Me | null;
  loading: boolean;
  /** Parol bilan kirish. Login/parol noto'g'ri bo'lsa `null`, aks holda foydalanuvchi. */
  login: (phone: string, password: string) => Promise<Me | null>;
  /** OTP tasdiqlangandan keyin chaqiriladi (tokenlar tayyor). Foydalanuvchini qaytaradi. */
  completeLogin: (access: string, refresh: string) => Promise<Me | null>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthValue | null>(null);

export const STAFF_ROLES: Role[] = ["super_admin", "admin", "electrician"];
export const ADMIN_ROLES: Role[] = ["super_admin", "admin"];

const ME_KEY = ["me"] as const;

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  // Tokenlar tashqi (localStorage) holat: "bor/yo'q" ni shu yerda aks ettiramiz
  const [authed, setAuthed] = useState(() => tokens.getAccess() !== null);

  const fetchMe = useCallback(async (): Promise<Me> => {
    try {
      return unwrap(await api.GET("/api/auth/me/"));
    } catch (e) {
      tokens.clear(); // yaroqsiz/eskirgan sessiya
      setAuthed(false);
      throw e;
    }
  }, []);

  const me = useQuery({
    queryKey: ME_KEY,
    queryFn: fetchMe,
    enabled: authed,
    retry: false,
    staleTime: Infinity,
  });
  const user = authed ? (me.data ?? null) : null;
  const loading = authed && me.isPending;

  useEffect(() => {
    const onLost = () => {
      setAuthed(false);
      qc.clear();
    };
    window.addEventListener(AUTH_LOST_EVENT, onLost);
    return () => window.removeEventListener(AUTH_LOST_EVENT, onLost);
  }, [qc]);

  const completeLogin = useCallback(
    async (access: string, refresh: string) => {
      tokens.set(access, refresh);
      setAuthed(true);
      try {
        return await qc.fetchQuery({ queryKey: ME_KEY, queryFn: fetchMe, staleTime: 0 });
      } catch {
        return null;
      }
    },
    [qc, fetchMe],
  );

  const login = useCallback(
    async (phone: string, password: string) => {
      const res = await api.POST("/api/auth/login/", { body: { phone, password } });
      if (res.response.status === 401) return null;
      const data = unwrap(res);
      return completeLogin(data.access, data.refresh);
    },
    [completeLogin],
  );

  const logout = useCallback(async () => {
    const refresh = tokens.getRefresh();
    if (refresh) await api.POST("/api/auth/logout/", { body: { refresh } }).catch(() => undefined);
    tokens.clear();
    setAuthed(false);
    qc.clear();
  }, [qc]);

  const value = useMemo(
    () => ({ user, loading, login, completeLogin, logout }),
    [user, loading, login, completeLogin, logout],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth AuthProvider ichida ishlatilishi kerak");
  return v;
}

/** Kirgandan keyin foydalanuvchi qaysi sahifaga tushadi: xodim — panel, uy egasi — /my. */
export function homeFor(role: Role, from?: string): string {
  if (role === "customer") return from?.startsWith("/my") ? from : "/my";
  return from && !from.startsWith("/my") ? from : "/";
}

export const isAdmin = (user: Me | null) => !!user && ADMIN_ROLES.includes(user.role);
